import React, { useMemo } from 'react';
import { Card, CardBody, CardHeader, Button } from 'reactstrap';
import { Icon } from 'js/components';
import { Loading } from 'js/components';
import { PanelHeader } from 'js/components';
import { Breadcrumbs } from 'js/components/Breadcrumbs';
import { useParams } from 'react-router-dom';
import EditConnectorConfiguration from './EditConnectorConfiguration';
import { faPlug } from '@fortawesome/pro-regular-svg-icons';
import ServiceCard from './ServiceCard';
import { CONNECTOR_SERVICES } from 'js/data/connectors';
import styles from './TeamSettings.module.scss';

function ConnectorCard({ connector }) {
  const { workspaceId, teamId } = useParams();

  const service = useMemo(() => {
    return CONNECTOR_SERVICES.find((service) => service.id === connector.type);
  }, [connector.type]);

  return (
    <ServiceCard
      service={service}
      connector={connector}
      to={`/w/${workspaceId}/teams/${teamId}/settings/connectors/new/${connector.id}`}
    />
  );
}

function NewConnectorConfiguration({
  team,
  availableConnectors,
  selectedConnector,
  onCancel,
  onSaveConfiguration = () => {},
  onDeleteConfiguration = () => {},
  isLoading = false,
}) {
  const { workspaceId, teamId } = useParams();

  const breadcrumbs = useMemo(
    () => [
      {
        label: 'Teams',
        to: `/w/${workspaceId}/teams`,
      },
      {
        label: team?.name,
        to: `/w/${workspaceId}/teams/${teamId}`,
      },
      {
        label: 'Settings',
        to: `/w/${workspaceId}/teams/${teamId}/settings`,
      },
    ],
    [workspaceId, teamId, team?.name]
  );

  // Determine if we should show the configuration form or the connector list
  if (isLoading) {
    return (
      <div>
        <PanelHeader size="flex">
          <div className="panel-body-no-label px-4">
            <Breadcrumbs breadcrumbs={breadcrumbs} />
            <div>
              <h2>Configure a new connector</h2>
            </div>
          </div>
        </PanelHeader>
        <div className="content">
          <Card>
            <CardBody>
              <Loading />
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  // If connectorId is specified and connector is found, show configuration form
  if (selectedConnector) {
    return (
      <EditConnectorConfiguration
        team={team}
        currentConnector={selectedConnector}
        currentConfiguration={null} // This is a new configuration
        onSaveConfiguration={onSaveConfiguration}
        onDeleteConfiguration={onDeleteConfiguration}
        onCancel={onCancel}
        isLoading={isLoading}
      />
    );
  }

  // Show connector selection list
  return (
    <div>
      <PanelHeader size="flex">
        <div className="panel-body-no-label px-4">
          <Breadcrumbs breadcrumbs={breadcrumbs} />
          <div>
            <h2>Configure a new connector</h2>
          </div>
        </div>
      </PanelHeader>
      <div className="content">
        <Card>
          <CardHeader>
            <p className="text-muted mb-4">
              Choose a connector to configure for this team. Each connector can
              only be configured once per team.
            </p>
          </CardHeader>
          <CardBody>
            {availableConnectors.length > 0 ? (
              <div className={styles['services-list']}>
                {availableConnectors.map((connector) => (
                  <ConnectorCard key={connector.id} connector={connector} />
                ))}
              </div>
            ) : (
              <div className="media text-muted">
                <Icon icon={faPlug} size="4x" />
                <div className="media-body">
                  <p>
                    All available connectors have been configured for this team.
                  </p>
                  <Button color="outline-primary" onClick={onCancel}>
                    Back to Settings
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

export default NewConnectorConfiguration;
